/**
 * ReportScreenshot — the screenshot a shake-report reporter opted to attach,
 * shown inline in the technical/feedback detail modal.
 *
 * The document only carries a bucket path (`screenshotPath`); the object itself
 * is admin-only, so there is nothing to put in an `<img src>` until the
 * `getFeedbackScreenshotUrl` callable mints a signed URL for it. That URL
 * expires after an hour, which is shorter than a detail modal can plausibly sit
 * open, so the URL is treated as refreshable rather than fetch-once:
 *
 *  - opening the full-size view re-signs first if the URL is at/near expiry;
 *  - a load failure on the thumbnail re-signs once and retries, which covers
 *    clock skew between this browser and the signing server (the expiry check
 *    is only as good as the local clock).
 *
 * Failures are rendered, not thrown: a non-super-admin gets a plain sentence
 * instead of a broken image, because every other control in the modal still
 * works for them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getFeedbackScreenshotUrl } from '../../services/firebase';
import { ImageLightbox } from '../common/ImageLightbox';

interface ReportScreenshotProps {
  /** Undefined when the reporter declined to attach one. */
  screenshotPath?: string;
  /** Used as the lightbox caption. */
  reportLabel?: string;
}

/**
 * Re-sign this far ahead of the stated expiry. Covers the round trip plus mild
 * clock skew; `onError` is the backstop for skew larger than this.
 */
const EXPIRY_SAFETY_MS = 60 * 1000;

interface SignedState {
  url: string;
  expiresAt: number;
}

function describeScreenshotError(err: unknown): string {
  const code =
    err && typeof err === 'object'
      ? (err as { code?: unknown }).code
      : undefined;

  // Callables surface their HttpsError code prefixed with `functions/`.
  const bare = typeof code === 'string' ? code.replace(/^functions\//, '') : '';

  switch (bare) {
    case 'permission-denied':
    case 'unauthenticated':
      return 'You need super-admin access to view report screenshots.';
    case 'not-found':
      return 'This screenshot is no longer stored.';
    case 'invalid-argument':
      return 'This report has a malformed screenshot path.';
    default:
      return 'Could not load the screenshot.';
  }
}

export function ReportScreenshot({
  screenshotPath,
  reportLabel,
}: ReportScreenshotProps) {
  const [signed, setSigned] = useState<SignedState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  /**
   * One automatic retry per signed URL. Without this an object that 404s at the
   * CDN would re-sign on every `onError`, and each new URL fires another error.
   */
  const retriedRef = useRef(false);
  /** Guards against a resolved fetch writing state after unmount. */
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const sign = useCallback(async (): Promise<SignedState | null> => {
    if (!screenshotPath) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await getFeedbackScreenshotUrl(screenshotPath);
      if (!aliveRef.current) return result;
      // Deliberately does NOT clear `retriedRef`: the automatic retry re-signs
      // through here, so clearing it would hand the next `onError` a fresh
      // budget and an object that always fails would re-sign forever. Only an
      // explicit Retry, or a new path, refills it.
      setSigned(result);
      return result;
    } catch (err) {
      if (aliveRef.current) {
        setSigned(null);
        setError(describeScreenshotError(err));
      }
      return null;
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [screenshotPath]);

  useEffect(() => {
    if (!screenshotPath) return;
    retriedRef.current = false;
    void sign();
  }, [screenshotPath, sign]);

  /** Re-sign when the current URL is expired, missing, or nearly expired. */
  const freshUrl = useCallback(async (): Promise<string | null> => {
    if (signed && Date.now() < signed.expiresAt - EXPIRY_SAFETY_MS) {
      return signed.url;
    }
    const next = await sign();
    return next?.url ?? null;
  }, [signed, sign]);

  const handleOpen = useCallback(async () => {
    const url = await freshUrl();
    if (url) setLightboxUrl(url);
  }, [freshUrl]);

  const handleImageError = useCallback(() => {
    // A URL that was signed before the modal was left open overnight, or a
    // clock further out than EXPIRY_SAFETY_MS, lands here rather than in the
    // expiry check. Re-sign once; if the retry also fails, show the error.
    if (retriedRef.current) {
      setSigned(null);
      setError('Could not load the screenshot.');
      return;
    }
    retriedRef.current = true;
    void sign();
  }, [sign]);

  // Nothing attached — render nothing at all, not an empty section.
  if (!screenshotPath) return null;

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2">Screenshot</h4>

      {loading && !signed && (
        <div className="h-32 w-[200px] animate-pulse rounded-lg bg-gray-200" />
      )}

      {!loading && error && (
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">{error}</p>
          <button
            type="button"
            onClick={() => {
              retriedRef.current = false;
              void sign();
            }}
            className="text-sm text-brand-600 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {signed && (
        <button
          type="button"
          onClick={handleOpen}
          title="Click to view full size"
          className="block overflow-hidden rounded-lg border border-gray-200 transition hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <img
            src={signed.url}
            alt="Screenshot attached to this report"
            onError={handleImageError}
            className="block w-[200px] object-contain"
          />
        </button>
      )}

      <ImageLightbox
        src={lightboxUrl}
        filename={reportLabel ?? 'Report screenshot'}
        onClose={() => setLightboxUrl(null)}
      />
    </div>
  );
}
